import { defineStore } from "pinia";
import router from "../router";
import type { QuizData, Answer, Lead } from "../types";
import { dispatchQuizStartIfFresh, primeSiteSlugForEvents } from "../analytics/events";
import { waitForExternalSessionCookie, getSessionId } from "../analytics/session";
import { getVariantIdForSession } from "../analytics/variant";
import {
  captureSessionAttributes,
  __EMPTY_SESSION_ATTRIBUTES,
  type SessionAttributes,
} from "../analytics/session-attributes";
import { applyVariant } from "./variants";
import { skipUtmParams, navigateTop } from "../components/redirect";
import { resolveRedirectUrl } from "./redirect-resolution";
import { validateFlowConfig } from "../flow/schema";
import { hasTemplate, getTemplate } from "../templates";
import { setExposureContext, trackFlowError } from "../analytics/events";
import type { FlowScreen } from "../types";

const API_URLS = {
  LEADCHECK: "https://wgtk52k6dqdr5barbgeftvxcye0lqzlw.lambda-url.us-east-1.on.aws/", // new leadclump-quiz-api function urls
  LEAD: "https://fhuezwgl4usr2pxjfxyhjlacgm0coxbh.lambda-url.us-east-1.on.aws/", // new leadclump-quiz-api function urls
  LEADCHECK_BY_HASHED_EMAIL: "https://vu4a4euzjxlsr4cecxfxhv5efq0ilgkk.lambda-url.us-east-1.on.aws/" // new leadclump-quiz-api function urls
};

const FETCH_QUIZ_DATA_URL = (site: string, quizId: string) =>
  `https://hlkl4tw3vd.execute-api.us-east-1.amazonaws.com/prod/site/${site}/quiz/${quizId}/public`;

async function loadQuizData(site: string, quizId: string): Promise<QuizData> {
  let quizDataLocal: QuizData;

  try {
    quizDataLocal = (await import("../quiz.json")).default as unknown as QuizData;
  } catch {
    quizDataLocal = {} as QuizData;
  }

  let quizData = quizDataLocal;
  const isDevelopment = import.meta.env.MODE === "development";

  if (isDevelopment && Object.keys(quizData).length > 0) {
    quizData = quizDataLocal;
  } else {
    try {
      console.log("fetching quizData from API...")
      quizData = await fetchQuizDataFromAPI(site, quizId);
    } catch {
      quizData = {} as QuizData;
    }
  }

  if (quizData.facebookpixels && !Array.isArray(quizData.facebookpixels)) {
    quizData.facebookpixels = [quizData.facebookpixels];
  }
  if (quizData.tiktokpixels && !Array.isArray(quizData.tiktokpixels)) {
    quizData.tiktokpixels = [quizData.tiktokpixels];
  }
  if (quizData.googleads && !Array.isArray(quizData.googleads)) {
    quizData.googleads = [quizData.googleads];
  }

  if (import.meta.env.MODE === "development") {
    quizData.url = "http://localhost:5173";
  }
  return quizData;
}

async function fetchQuizDataFromAPI(site: string, quizId: string): Promise<QuizData> {
  try {
    const response = await fetch(FETCH_QUIZ_DATA_URL(site, quizId));
    if (!response.ok) {
      console.error("Error loading quiz.json from API");
      return {} as QuizData;
    }
    return await response.json();
  } catch (error) {
    console.error("Connection error when trying to load quiz.json from API:", error);
    return {} as QuizData;
  }
}

export const useMainStore = defineStore({
  id: "main",
  state: () => ({
    scriptsLoaded: false,
    redirecting: false,
    fetching: false,
    questionAnswers: [] as Answer[],
    currentIndex: 0,
    formLead: { name: "", email: "", phone: "" },
    quizData: {} as QuizData,
    isLoading: true,
    currentStep: 1,
    totalSteps: 0,
    // Quiz-mount ad-attribution snapshot for Google Data Manager API
    // sessionAttributes match-rate uplift. Populated once at App mount via
    // _captureSessionAttributes(), sent inside createLead payload at submit.
    // Default is an all-empty object so consumers never have to null-check.
    // See src/analytics/session-attributes.ts for the capture contract.
    sessionAttributes: { ...__EMPTY_SESSION_ATTRIBUTES } as SessionAttributes,
    // DEV-1135 flow engine. 'legacy' renders the untouched RouterView path;
    // 'flow' renders FlowRenderer over flowScreens with the template shell.
    // Set once per session by _resolveFlowConfig inside initializeQuizData.
    flowMode: 'legacy' as 'legacy' | 'flow',
    flowScreens: [] as FlowScreen[],
    templateId: '',
    // Position within flowScreens (flow mode only). Independent from
    // currentIndex — answer recording is decoupled from advancement so
    // interstitials/reordering can't desync question identity.
    flowIndex: 0,
  }),
  getters: {
    currentQuestion(state) {
      return state.quizData.questions?.[state.currentIndex] ?? null;
    },
    questionsLength(state) {
      return state.quizData.questions?.length || 0;
    },
    progressBarPercentage: (state) => {
      if (!state.totalSteps) return 0;
      const percentage = Math.round((state.currentStep / state.totalSteps) * 100);
      return Math.min(percentage, 100);
    },
    isLastStep(): boolean {
      return this.currentIndex === this.questionsLength;
    },
    allQuestionsAnswered(state) {
      return (state.questionAnswers.length > 0 && state.questionAnswers.length === (state.quizData.questions?.length || 0));
    },
    quizQuestions(state) {
      return state.quizData.questions;
    },
    quizUrlLogic(state) {
      return state.quizData.url_logic;
    },
    gateData(state) {
      return state.quizData.gate;
    },
    getQuestionById(state) {
      return (question_id: string) => state.quizData.questions?.find((q) => q.id === question_id);
    },
    getLeadAnswers(state) {
      return state.questionAnswers.map((qa) => ({
        id: qa[0],
        answer: qa[1]
      }));
    },
    getLeadAnswerByQuestionId(state) {
      return (question_id: string) => state.questionAnswers.filter((qa) => qa[0] === question_id).map((qa) => qa[1]);
    },
    // Flow-mode progress: position among QUESTION screens only (matches the
    // UTUA "1 - 4" convention — gate/redirect don't count). DEV-1135.
    flowQuestionProgress(state): { current: number; total: number } {
      const isQ = (s: FlowScreen) => s.type === 'question' || s.type === 'question-gate';
      const total = state.flowScreens.filter(isQ).length;
      const current = state.flowScreens
        .slice(0, state.flowIndex + 1)
        .filter(isQ).length;
      return { current: Math.min(current || 1, total || 1), total };
    },
    currentFlowScreen(state): FlowScreen | null {
      return state.flowScreens[state.flowIndex] ?? null;
    },
  },
  actions: {
    /**
     * Capture the quiz-mount ad-attribution snapshot. Called once at App.vue
     * `<script setup>` BEFORE checkRoutes, so query params and DOM state are
     * the originals (before any future router guard could mutate them).
     *
     * Idempotency lives in the underlying module's `cached` (single source of
     * truth). The store action just assigns state on first call; subsequent
     * calls re-assign the same cached reference.
     *
     * Wrapped in try/catch so a module-level throw cannot break App mount.
     */
    _captureSessionAttributes(): void {
      try {
        this.sessionAttributes = captureSessionAttributes();
      } catch {
        // Leave the all-empty default in place. Payload still sends the block;
        // CH rows get empty strings for this session rather than dropped attribution.
      }
    },
    async initializeQuizData(site: string, quizId: string) {
      this.isLoading = true;
      // Run the leadclump-flow cookie wait in parallel with the quiz data fetch:
      // 1. The wait is independent of network IO, so overlapping costs nothing.
      // 2. Both must complete before we flip isLoading=false, otherwise Vue's
      //    reactive flush could mount child components and let them fire analytics
      //    (which calls getSessionId) before leadclump-flow's pblk_sid lands —
      //    locking sessionIdSource to 'generated' for the rest of the session.
      // 3. applyVariant() below ALSO depends on this order: it reads the real
      //    pblk_sid to bucket A/B/control. Calling it before the wait would
      //    lock the variant cache to 'control' for the session and silently
      //    disable B rendering. Tests in store/__tests__/variants.spec.ts
      //    pin this invariant.
      const [baseConfig] = await Promise.all([
        loadQuizData(site, quizId),
        waitForExternalSessionCookie(),
      ]);
      // Prime cachedSiteSlug BEFORE applyVariant so any variants_error event
      // emitted from a malformed override carries the right site_slug.
      // dispatchQuizStartIfFresh below also calls this internally — harmless
      // because deriveSiteSlug is pure and idempotent.
      primeSiteSlugForEvents(baseConfig);
      // First bucketing call of the session: carries the configured B share
      // (DEV-1737). Every later call reads the cached decision.
      const variantId = getVariantIdForSession(getSessionId(), baseConfig.variant_b_pct);
      this.quizData = applyVariant(baseConfig, variantId);
      // Resolve flow/template BEFORE quiz_start fires so the exposure fields
      // (template_id, flow_fallback) are truthful on every event including the
      // first. DEV-1135.
      this._resolveFlowConfig();
      this.isLoading = false;
      dispatchQuizStartIfFresh(this.quizData);
      this.checkRoutes();
      this.totalSteps = (this.quizData?.questions?.length) + 1 || 0;
    },
    /**
     * Decide legacy vs flow rendering from the (runtime-fetched) quiz config.
     * Containment posture mirrors applyVariant's variants_error pattern: ANY
     * invalid flow/template config falls back to the untouched legacy renderer
     * and fires an alertable flow_error event — a DDB edit must never brick a
     * live quiz. DEV-1135.
     */
    _resolveFlowConfig(): void {
      const quizData = this.quizData;
      const wantsFlow = !!(quizData?.flow || quizData?.template_id);
      if (!wantsFlow) {
        setExposureContext({ template_id: 'default', flow_fallback: false });
        return;
      }
      try {
        const errors: string[] = [];
        const templateId = quizData.template_id || '';
        if (!templateId || !hasTemplate(templateId)) {
          errors.push(`unknown or missing template_id "${templateId}"`);
        }
        if (!quizData.flow) {
          errors.push('template_id set but flow missing — flow is required for templated quizzes');
        }
        let screens: FlowScreen[] = [];
        if (quizData.flow) {
          const result = validateFlowConfig(quizData);
          if (!result.ok) errors.push(...result.errors);
          screens = result.screens;
        }
        // Block coverage: the template must provide a component for every
        // screen type the flow uses (e.g. a slider-only template cannot
        // render plain question screens).
        const tpl = templateId ? getTemplate(templateId) : undefined;
        if (tpl) {
          for (const s of screens) {
            if (s.type === 'question' && !tpl.question) {
              errors.push(`template "${templateId}" has no question component for screen "${s.id}"`);
            } else if (s.type === 'gate' && !tpl.gate) {
              errors.push(`template "${templateId}" has no gate component for screen "${s.id}"`);
            } else if (s.type === 'question-gate' && !tpl.questionGate) {
              errors.push(`template "${templateId}" has no questionGate component for screen "${s.id}"`);
            } else if (s.type === 'form-gate' && !tpl.formGate) {
              errors.push(`template "${templateId}" has no formGate component for screen "${s.id}"`);
            }
          }
        }
        if (errors.length > 0) {
          console.error('[flow] invalid flow/template config, falling back to legacy:', errors);
          setExposureContext({ template_id: 'default', flow_fallback: true });
          trackFlowError({
            quiz_id: quizData.quiz_id || '',
            error_message: errors.join('; '),
          });
          return;
        }
        this.flowMode = 'flow';
        this.flowScreens = screens;
        this.templateId = quizData.template_id as string;
        this.flowIndex = 0;
        setExposureContext({ template_id: this.templateId, flow_fallback: false });
      } catch (error) {
        console.error('[flow] resolution threw, falling back to legacy:', error);
        setExposureContext({ template_id: 'default', flow_fallback: true });
        try {
          trackFlowError({
            quiz_id: quizData?.quiz_id || '',
            error_message: error instanceof Error ? error.message : String(error),
          });
        } catch { /* analytics must never break the quiz */ }
      }
    },
    /**
     * Flow-engine answer recording — decoupled from advancement (FlowRenderer
     * owns navigation). Re-answering a question (back-nav) REPLACES the prior
     * answer instead of appending, so question_answers carries at most one
     * answer per question id. DEV-1135.
     */
    recordFlowAnswer(questionAnswer: Answer) {
      const existingIndex = this.questionAnswers.findIndex(
        ([qId]) => qId === questionAnswer[0]
      );
      if (existingIndex >= 0) {
        this.questionAnswers.splice(existingIndex, 1, questionAnswer);
      } else {
        this.questionAnswers.push(questionAnswer);
      }
    },
    pushQuestionAnswer(questionAnswer: Answer) {
      const isDuplicate = this.questionAnswers.some(
        ([qId, answer]) => qId === questionAnswer[0] && answer === questionAnswer[1]
      );

      if (!isDuplicate) {
        this.questionAnswers.push(questionAnswer);

        if (this.currentIndex < this.questionsLength - 1) {
          this.currentIndex += 1;
          this.currentStep = this.currentIndex + 1;
        }
      }
    },
    async apiRequest(url: string, method: string, data: unknown = null) {
      this.fetching = true;
      const options: RequestInit = {
        method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: data ? JSON.stringify(data) : undefined,
      };

      try {
        const response = await fetch(url, options);
        if (response.ok)
          return await response.json();
        else
          throw response
      } catch (error) {
        console.error(error);
        return error;
      } finally {
        this.fetching = false;
      }
    },
    async checkLead(email: string, site: string): Promise<unknown> {
      this.fetching = true;
      try {
        const response = await fetch(API_URLS.LEADCHECK, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, site }),
        });

        if (!response.ok) {
          throw new Error(`Request error: ${response.status} - ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Verification result:', result);
        return result;
      } catch (error) {
        console.error('Error checking lead:', error);
        throw error;
      } finally {
        this.fetching = false;
      }
    },
    isLeadCheckValid(response: { valid: boolean; safe: boolean }) {
      return response.valid && response.safe;
    },
    async createLead(lead: Lead) {
      const result = await this.apiRequest(API_URLS.LEAD, "POST", lead);
      return result;
    },
    async retrieveLeadcheckByHashedEmail() {
      const hashed_email = this._getCookie('leadclump_e_sha256');
      if (hashed_email) {
        const result = await this.apiRequest(API_URLS.LEADCHECK_BY_HASHED_EMAIL, "POST", {"hashed_email": hashed_email});
        return result;
      }
      return null;
    },
    resetQuiz() {
      this.redirecting = true;
      this.questionAnswers = [];
      this.currentIndex = 0;
    },
    redirectToLanding() {
      const quizData = this.quizData;
      const leadAnswers = this.getLeadAnswers;

      // Resolution extracted to the pure, regression-pinned resolveRedirectUrl
      // (shared with the gate money path in useLeadSubmit). DEV-1135.
      const direct_to = resolveRedirectUrl(
        this.quizUrlLogic,
        leadAnswers.map((answer) => answer.answer)
      );

      const finalUrl = direct_to || quizData.quiz_default_url;
      
      // Adiciona parâmetros da URL atual
      const queryParams = new URLSearchParams(window.location.search);
      const urlParams = new URLSearchParams();
      
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'sub_id'].forEach(param => {
        const value = queryParams.get(param);
        if (value) urlParams.set(param, value);
      });

      // Answer-level skip: this action only runs when an answer carries skip=true
      // (Question.vue), i.e. the user jumped straight to the landing page without
      // finishing the quiz or seeing the gate. Stamp last so it overrides the
      // inbound UTM it relabels. DEV-1418.
      Object.entries(skipUtmParams(quizData)).forEach(([param, value]) => {
        urlParams.set(param, value);
      });

      const redirect_url = `${finalUrl}?${urlParams.toString()}`;

      this.resetQuiz();
      navigateTop(redirect_url);
    },
    checkRoutes() {
      // Flow mode renders FlowRenderer directly in App.vue — vue-router stays
      // parked on '/' and must not bounce between legacy routes. DEV-1135.
      if (this.flowMode === 'flow') return;
      if (this.allQuestionsAnswered) {
        this.currentStep = this.totalSteps;
        router.push('/gate');
        return;
      }
      router.push({ name: 'question', query: router.currentRoute.value.query });
    },
    _getCookie(name: string): string {
      try {
        const value = document.cookie
          .split('; ')
          .find(row => row.startsWith(name + '='))
          ?.split('=')[1];
        return value ? decodeURIComponent(value) : '';
      } catch (e) {
        console.error(e);
        return '';
      }
    },
    _setCookie(name: string, value: string, days: number) {
      try {
        let expires = ""
        if (days) {
          const date = new Date()
          date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
          expires = "; expires=" + date.toUTCString()
        }
        // Mirror src/analytics/session.ts: cross-site embed support requires
        // SameSite=None; Secure inside iframes on HTTPS. Browsers reject
        // SameSite=None without Secure, so on http (localhost dev) fall back
        // to Lax. Keeps both cookie writers consistent so a future audit can
        // reason about both with one mental model.
        //
        // Note on TTL: Safari ITP caps document.cookie-set cookies at 7 days
        // of storage regardless of the expires attribute. So a `days=30` call
        // resolves to ~7 days on Safari/iOS — the 30 is aspirational on
        // Chromium/Firefox and a floor of 7 on WebKit. Plan capacity (e.g.
        // GAds EC match-window) accordingly.
        const isSecure = typeof location !== 'undefined' && location.protocol === 'https:'
        const sameSite = isSecure ? 'None; Secure' : 'Lax'
        // Encode the value so attacker-controlled inputs (e.g. a gclid like
        // `; path=/; domain=.evil.com`) cannot inject extra cookie attributes.
        // Symmetric with _getCookie above, which decodeURIComponent-s the read.
        // Real-world values (hex digests, Google gclids) are URL-safe and pass
        // through unchanged.
        const encoded = encodeURIComponent(value == null ? "" : String(value))
        document.cookie =
          `${name}=${encoded}${expires}; path=/; SameSite=${sameSite}`
      } catch (e) {
        console.error(e);
      }
    }
  },
});
